import { ShowingStatus, AccessType } from "@utils/enums";
import { EventShowing, EventShowingsMsgs } from "@types";
import { format, parseISO } from "date-fns";
import { de, es } from "date-fns/locale";
import { BasketEvent } from "@features/cartDetails/types";
import { getCookieValue } from "@utils/helpers";
import { useTranslations } from "next-intl";

export const handleShowingType = (
  time: EventShowing,
  messages?: EventShowingsMsgs["messages"],
  translations?: ReturnType<typeof useTranslations<"EventPage.statuses">>
) => {
  const locale = getCookieValue("NEXT_LOCALE");

  const titleShowing =
    messages
      ?.filter((msg) => msg.type === "button")
      .find((msg) => msg.status === time.status.id)?.message ?? "";

  const descriptionShowing =
    messages
      ?.filter((msg) => msg.type === "message")
      .find((msg) => msg.status === time.status.id)?.message ?? "";

  if (
    time.status.id === ShowingStatus.SOLDOUT ||
    time.status.id === ShowingStatus.ADMINSOLDOUT
  ) {
    return {
      bgColor: "bg-[color:var(--red-a3)]",
      textColor: "text-[color:var(--red-a11)]",
      color: "red",
      title:
        titleShowing.trim() || translations?.("soldout.heading") || "Sold out",
      description:
        descriptionShowing.trim() ||
        translations?.("soldout.description") ||
        "This showing is sold out.",
      selectable: false,
    };
  }

  if (time.status.id === ShowingStatus.CANCELLED) {
    return {
      bgColor: "bg-[color:var(--red-a3)]",
      textColor: "text-[color:var(--red-a11)]",
      color: "red",
      title:
        titleShowing.trim() ||
        translations?.("cancelled.heading") ||
        "Cancelled",
      description:
        descriptionShowing.trim() ||
        translations?.("cancelled.description") ||
        "This showing is cancelled.",
      selectable: false,
    };
  }

  if (time.status.id === ShowingStatus.POSTPONED) {
    return {
      bgColor: "bg-[color:var(--orange-a3)]",
      textColor: "text-[color:var(--orange-a11)]",
      color: "orange",
      title:
        titleShowing.trim() ||
        translations?.("postponed.heading") ||
        "Postponed",
      description:
        descriptionShowing.trim() ||
        translations?.("postponed.description") ||
        "This showing is postponed.",
      selectable: false,
    };
  }

  if (time.status.id === ShowingStatus.NOTYETONSALE) {
    return {
      bgColor: "bg-[color:var(--orange-a3)]",
      textColor: "text-[color:var(--orange-a11)]",
      color: "orange",
      title:
        titleShowing.trim() ||
        translations?.("notyetonsale.heading") ||
        "Not on sale",
      description:
        descriptionShowing.trim() ||
        translations?.("notyetonsale.description") ||
        "This showing is not yet on sale",
      selectable: false,
    };
  }

  if (time.status.id === ShowingStatus.AVAILABLE) {
    const title = messages
      ?.filter((msg) => msg.type === "button")
      .find(
        (msg) =>
          msg.access === time.access.type &&
          msg.status === ShowingStatus.AVAILABLE
      )?.message;

    const description = messages
      ?.filter((msg) => msg.type === "message")
      .find(
        (msg) =>
          msg.access === time.access.type &&
          msg.status === ShowingStatus.AVAILABLE
      )?.message;

    switch (time.access.type) {
      case AccessType.PUBLIC:
        return {
          bgColor: "bg-[color:var(--green-a3)]",
          textColor: "text-[color:var(--green-a11)]",
          color: "green",
          selectable: true,
        };
      case AccessType.PASSWORD:
        return {
          bgColor: "bg-[color:var(--green-a3)]",
          textColor: "text-[color:var(--green-a11)]",
          color: "green",
          title: title || translations?.("password.heading") || "Password",
          description:
            description ||
            translations?.("password.description") ||
            "Password is required",
          selectable: true,
        };
      case AccessType.SALESCLOSED:
        return {
          bgColor: "bg-[color:var(--red-a3)]",
          textColor: "text-[color:var(--red-a11)]",
          color: "red",
          title:
            title || translations?.("salesclosed.heading") || "Sales closed.",
          description:
            description ||
            translations?.("salesclosed.description") ||
            "Sales closed.",
          selectable: false,
        };
      case AccessType.DOORSALES:
        return {
          bgColor: "bg-[color:var(--orange-a3)]",
          textColor: "text-[color:var(--orange-a11)]",
          color: "orange",
          title: title || translations?.("doorsales.heading") || "Door Sales",
          description:
            description ||
            translations?.("doorsales.description") ||
            "Door sales may still be available.",
          selectable: false,
        };
      case AccessType.NOTYETONSALE:
        return {
          bgColor: "bg-[color:var(--orange-a3)]",
          textColor: "text-[color:var(--orange-a11)]",
          color: "orange",
          title: title
            ? title.replace(
                "{date}",
                time.access.date.public.local.start.formatted ??
                  format(
                    parseISO(time.access.date.public.local.start.raw),
                    `EEE d MMM yyyy h:mm aa`,
                    {
                      locale:
                        locale === "de" ? de : locale === "es" ? es : undefined,
                    }
                  )
              )
            : translations?.("notonsale.heading") || "Not on sale",
          description: description
            ? description.replace(
                "{date}",
                time.access.date.public.local.start.formatted ??
                  format(
                    parseISO(time.access.date.public.local.start.raw),
                    `EEE d MMM yyyy h:mm aa`,
                    {
                      locale:
                        locale === "de" ? de : locale === "es" ? es : undefined,
                    }
                  )
              )
            : translations?.("notyetonsale.heading") || "Not yet on sale",
          selectable: false,
        };
      case AccessType.EXPIRED:
        return {
          bgColor: "bg-[color:var(--red-a3)]",
          textColor: "text-[color:var(--red-a11)]",
          color: "red",
          title: title || translations?.("expired.heading") || "Expired",
          description:
            description ||
            translations?.("expired.description") ||
            "This ticket has expired.",
          selectable: false,
        };
    }
  }

  if (time.status.id === ShowingStatus.FREEENTRY) {
    return {
      bgColor: "bg-[color:var(--green-a3)]",
      textColor: "text-[color:var(--green-a11)]",
      color: "green",
      title:
        titleShowing.trim() ||
        translations?.("freeentry.heading") ||
        "Free Entry",
      description:
        descriptionShowing.trim() ||
        translations?.("freeentry.description") ||
        "This is a free entry event. You do not need a ticket.",
      selectable: false,
    };
  }

  return {
    bgColor: "bg-[color:var(--accent-a3)]",
    textColor: "text-[color:var(--accent-a11)]",
    color: undefined,
    title: titleShowing.trim(),
    description: descriptionShowing.trim(),
    selectable: false,
  };
};

export const bookedShowingTickets = (
  id: number,
  basketEvent: BasketEvent | undefined
) =>
  basketEvent &&
  basketEvent.Showings.filter((showing) => showing.Id === id).map(
    (showing) => showing.Items.length
  )[0];
